/// <amd-module name="Artifacts/FeedSettings/components/DetailsTab" />

import { IDetailTabErrors } from "Artifacts/FeedSettings/hooks/DetailsTabForm";
import * as Res from "Artifacts/FeedSettings/Resources";
import { IFeedSettings } from "Artifacts/FeedSettings/types";
import { IDetailsTabFormState } from "Artifacts/FeedSettings/types/IDetailsTabFormState";
import { DefaultLoader } from "Artifacts/Root/components/DefaultLoader";
import { FWLink } from "Artifacts/Root/components/FWLink";
import { ComponentState } from "Artifacts/Root/Constants";
import { isLoading } from "Artifacts/Root/helpers/ComponentStateHelpers";
import { IFeed } from "Artifacts/Root/types";
import * as React from "react";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { Checkbox } from "VSSUI/Checkbox";
import { FormItem } from "VSSUI/FormItem";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";

export interface IDetailsTabProps {
  componentState: ComponentState;
  saveComponentState: ComponentState;
  feed?: IFeed;
  settings?: IFeedSettings;
  isUserAdmin: boolean;
  isFormDirty: boolean;
  formState: IDetailsTabFormState;
  resetForm: () => void;
  onFeedNameChange: (value: string) => void;
  onFeedDescriptionChange: (value: string) => void;
  onHideDeletedPackageVersionsChange: (value: boolean) => void;
  onBadgesEnabledChange: (value: boolean) => void;
  onRetentionPoliciesEnabledChange: (value: boolean) => void;
  onRetentionPolicyDaysLimitChange: (value: string) => void;
  onRetentionPolicyCountLimitChange: (value: string) => void;
  onSubmit: (formState: IDetailsTabFormState) => void;
  errors: IDetailTabErrors;
}

const maxFeedLength = 64;
const maxFeedDescriptionLength = 255;
const feedDescriptionFieldRows = 5;

export const DetailsTab: React.FC<IDetailsTabProps> = ({
  feed,
  componentState,
  saveComponentState,
  settings,
  formState,
  isUserAdmin,
  isFormDirty,
  onFeedNameChange,
  onFeedDescriptionChange,
  onHideDeletedPackageVersionsChange,
  onBadgesEnabledChange,
  onRetentionPoliciesEnabledChange,
  onRetentionPolicyCountLimitChange,
  onRetentionPolicyDaysLimitChange,
  resetForm,
  onSubmit,
  errors,
}) => {
  const formHeadingId = "feed-settings-details-form-title";
  const deletedPackagesId = "feed-settings-details-deleted-packages";
  const packageSharingId = "feed-settings-details-package-sharing";
  const retentionPoliciesId = "feed-settings-details-retention-policies";

  return (
    <DefaultLoader state={componentState}>
      <Card titleProps={{ text: Res.DetailsTab_Heading, ariaLevel: 2 }}>
        <div role="form" aria-labelledby={formHeadingId} className="feed-details-tab">
          <FormItem
            className="margin-bottom required"
            label={Res.DetailsTab_Name}
            error={errors.feedNameError != null}
            message={errors.feedNameError?.description}
          >
            <TextField
              className={"text-field"}
              value={formState.feedName}
              maxLength={maxFeedLength}
              onChange={(_event, value) => onFeedNameChange(value)}
              disabled={!isUserAdmin || feed?.isDeleted}
              required
            />
          </FormItem>

          <TextField
            className={"text-field margin-bottom"}
            label={Res.DetailsTab_Description}
            value={formState.feedDescription}
            multiline={true}
            maxLength={maxFeedDescriptionLength}
            rows={feedDescriptionFieldRows}
            resizable={false}
            autoAdjustHeight={true}
            onChange={(_event, value) => onFeedDescriptionChange(value)}
            disabled={!isUserAdmin || feed?.isDeleted}
          />
          {!feed?.isDeleted && (
            <>
              <section aria-labelledby={deletedPackagesId}>
                <h3 id={deletedPackagesId} className={"fontSizeL header margin-bottom"}>
                  {Res.DetailsTab_DeletedPackages}
                </h3>
                <p className={"fontSizeM margin-bottom"}>
                  <FormatComponent format={Res.DetailsTab_DeletedPackagesDescription}>
                    <FWLink linkId="851326" underline>
                      {Res.DetailsTab_FeedsAreImmutable}
                    </FWLink>
                  </FormatComponent>
                </p>
                <Checkbox
                  checked={formState.hideDeletedPackageVersions}
                  onChange={(_event, value) => onHideDeletedPackageVersionsChange(value)}
                  label={Res.DetailsTab_HideDeletedPackageVersions}
                  disabled={!isUserAdmin}
                />
              </section>
              <section aria-labelledby={packageSharingId}>
                <h3 id={packageSharingId} className={"fontSizeL header margin-bottom"}>
                  {Res.DetailsTab_PackageSharing}
                </h3>
                <p className={"fontSizeM margin-bottom"}>
                  {Res.DetailsTab_PackageBadgesDescription}
                </p>
                <Checkbox
                  checked={formState.badgesEnabled}
                  onChange={(_event, value) => onBadgesEnabledChange(value)}
                  label={Res.DetailsTab_EnablePackageBadges}
                  disabled={!isUserAdmin}
                />
              </section>
              <section aria-labelledby={retentionPoliciesId}>
                <h3 id={retentionPoliciesId} className={"fontSizeL header margin-bottom"}>
                  {Res.DetailsTab_RetentionPolicies_Title}
                </h3>
                <div>
                  <Checkbox
                    checked={formState.retentionPoliciesEnabled}
                    onChange={(_event, value) => onRetentionPoliciesEnabledChange(value)}
                    label={Res.DetailsTab_RetentionPolicies_EnabledDescription}
                    disabled={!isUserAdmin}
                  />

                  <p className={"fontSizeM margin-bottom"}>
                    {Res.DetailsTab_RetentionPolicies_DescriptionText}
                  </p>
                  <FormItem
                    className="margin-bottom"
                    label={Res.DetailsTab_RetentionPolicies_CountLimitLabel}
                    error={errors.retentionMaxNumVersionsError != null}
                    message={errors.retentionMaxNumVersionsError?.description}
                  >
                    <TextField
                      className={"text-field"}
                      value={
                        formState.retentionPoliciesEnabled
                          ? formState.retentionPolicyCountLimit
                          : ""
                      }
                      onChange={(_event, value) => onRetentionPolicyCountLimitChange(value)}
                      disabled={!isUserAdmin || !formState.retentionPoliciesEnabled}
                    />
                  </FormItem>
                  <FormItem
                    className="margin-bottom"
                    label={Res.DetailsTab_RetentionPolicies_DaysLimitLabel}
                    error={errors.retentionDaysToKeepError != null}
                    message={errors.retentionDaysToKeepError?.description}
                  >
                    <TextField
                      className={"text-field"}
                      value={
                        formState.retentionPoliciesEnabled ? formState.retentionPolicyDaysLimit : ""
                      }
                      onChange={(_event, value) => onRetentionPolicyDaysLimitChange(value)}
                      disabled={!isUserAdmin || !formState.retentionPoliciesEnabled}
                    />
                  </FormItem>
                </div>
              </section>
            </>
          )}
          {isUserAdmin && !feed?.isDeleted && (
            <div className="feed-details-footer">
              {/** make sure that if retention is unchecked, we don't send the retention values */}
              <Button
                primary={true}
                disabled={!isFormDirty || Object.keys(errors).length > 0}
                onClick={() => onSubmit(formState)}
                text={Res.FeedSettings_SaveButton_Label}
                iconProps={
                  isLoading(saveComponentState)
                    ? {
                        render: (className) => (
                          <Spinner className={className} size={SpinnerSize.small} />
                        ),
                      }
                    : undefined
                }
              />
              <Button
                disabled={!isFormDirty}
                onClick={resetForm}
                text={Res.FeedSettings_CancelButton_Label}
              />
            </div>
          )}
        </div>
      </Card>
    </DefaultLoader>
  );
};
